import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { HelpCircle, Menu, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CommandPalette } from "@/components/shared/command-palette";
import { useUserDisplay } from "@/hooks/use-user-display";
import { signOut, useSession } from "@/lib/auth-client";
import { hasRole, parseRole } from "@/lib/roles";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { AppearancePicker } from "./AppearancePicker";
import { useAppearance } from "./AppearanceProvider";
import { DesktopNav } from "./DesktopNav";
import { MobileNav } from "./MobileNav";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";

// "Data as of <date>" badge. Placeholder for now — once the nightly cache
// job lands, this will read the completion timestamp off the Param singleton
// (see [[memic-param-singleton]]) so users can see staleness when a job
// fails. Until then we just show yesterday's date so the chip looks real.
function DataAsOfBadge() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const label = yesterday.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return (
    <span
      className="hidden items-center rounded-md bg-primary-foreground/10 px-2 py-1 font-medium text-primary-foreground/80 text-xs md:inline-flex"
      title="Data is refreshed nightly from the source system."
    >
      Data as of {label}
    </span>
  );
}

// One-shot pull of the current user's saved appearance from UserDisplay.
// Runs once per authenticated session — the picker writes through to the
// server on every edit, so subsequent refetches return the value we just
// set and don't need to be reapplied. localStorage still hydrates the
// instant first paint before this effect fires.
function useSyncAppearanceFromServer() {
  const { setAccent, setBackground } = useAppearance();
  const { data: ud } = useUserDisplay();
  const synced = useRef(false);
  useEffect(() => {
    if (synced.current || !ud) return;
    synced.current = true;
    if (ud.appearanceAccent !== undefined) setAccent(ud.appearanceAccent);
    if (ud.appearanceBackground !== undefined) setBackground(ud.appearanceBackground);
  }, [ud, setAccent, setBackground]);
}

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userRole = parseRole(session?.user?.role);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useSyncAppearanceFromServer();

  // Global Cmd+K / Ctrl+K shortcut. Captured at the document level so the
  // palette opens from any focused element (including inputs). We avoid
  // overriding the browser's default save shortcut by checking the exact
  // key combo before preventing default.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const isAdmin = hasRole(userRole, ["admin"]);
  const canClaimsOps = hasRole(userRole, ["admin", "claims_ops"]);
  // Operations menu has three items: Claim Teams + Team History (claims_ops),
  // Team Help (everyone). Hide the menu entirely for users who would only see
  // Team Help — the label "Operations" promises more than a single help link.
  const showOperations = canClaimsOps;

  async function handleSignOut() {
    await signOut();
    // Drop every cached query so a subsequent sign-in (potentially as a
    // different user on the same browser) cannot flash the prior identity's
    // data before the fresh fetches land.
    queryClient.clear();
    router.navigate({ to: "/login" });
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <header className="relative z-40 flex h-14 shrink-0 items-center gap-3 border-primary-foreground/10 border-b bg-primary px-4 text-primary-foreground shadow-sm md:gap-6 md:px-6">
        {/* Absolutely-centered "Data as of" chip — sits visually in the
            middle of the header regardless of how wide the left nav or the
            right cluster get. pointer-events-none on the wrapper so it
            doesn't intercept clicks on anything behind it. */}
        <div className="-translate-x-1/2 pointer-events-none absolute left-1/2 z-10 hidden md:block">
          <DataAsOfBadge />
        </div>

        {/* Mobile nav trigger — only visible below md. */}
        <button
          type="button"
          aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
          aria-expanded={mobileNavOpen}
          aria-controls="mobile-nav"
          className="inline-flex size-9 items-center justify-center rounded-md text-primary-foreground/90 hover:bg-primary-foreground/10 md:hidden"
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <Menu className="size-5" />
        </button>

        {/* Brand */}
        <Link to="/home" className="flex shrink-0 items-center gap-2">
          <img src="/icons/memic-logo.svg?v=2" alt="MEMIC" className="h-6" />
          <span className="hidden font-medium text-primary-foreground/80 text-xs uppercase tracking-wider sm:inline">
            Customer 360
          </span>
        </Link>

        <DesktopNav
          pathname={location.pathname}
          isAdmin={isAdmin}
          showOperations={showOperations}
        />

        {/* Spacer so the user menu sits at the right when the primary nav is
            collapsed into the mobile drawer. */}
        <div className="ml-auto md:hidden" />

        {/* Right: search + theme + appearance + user menu */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label="Open search"
            title="Search (⌘K)"
            className="inline-flex h-8 items-center gap-2 rounded-md bg-primary-foreground/10 px-2 text-primary-foreground/80 text-sm hover:bg-primary-foreground/15 hover:text-primary-foreground"
          >
            <Search className="size-4" />
            <span className="hidden sm:inline">Search</span>
            <span className="hidden rounded border border-primary-foreground/20 bg-primary-foreground/10 px-1 py-px font-medium text-[10px] sm:inline">
              ⌘K
            </span>
          </button>
          <a
            href="/api/branding/user_guide/content"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open user guide (opens in new tab)"
            title="User guide (opens in new tab)"
            className="inline-flex size-8 items-center justify-center rounded-md bg-primary-foreground/10 text-primary-foreground/80 hover:bg-primary-foreground/15 hover:text-primary-foreground"
          >
            <HelpCircle className="size-4" />
          </a>
          <ThemeToggle />
          <AppearancePicker />
          {session?.user && (
            <UserMenu user={session.user} role={userRole} onSignOut={handleSignOut} />
          )}
        </div>

        {mobileNavOpen && (
          <MobileNav
            pathname={location.pathname}
            isAdmin={isAdmin}
            showOperations={showOperations}
            onClose={() => setMobileNavOpen(false)}
          />
        )}
      </header>

      <AnnouncementBanner />

      <main className="flex-1 overflow-auto p-4 md:p-6">{children}</main>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </div>
  );
}
